import React from "react";
import { CALENDAR_CONFIG } from "./calendarConstants.js";

/**
 * SkeletonLoader Component
 * Provides skeleton loading states for different sections
 */

/**
 * Calendar Skeleton - Shows skeleton for calendar grid
 */
export const CalendarSkeleton = () => {
    return (
        <div className="calendar-container p-4" style={{ position: 'relative' }}>
            {/* Calendar wrapper - matching .rbc-calendar structure */}
            <div
                className="rbc-calendar"
                style={{
                    borderRadius: '8px',
                    background: '#fff',
                    boxShadow: '0px 60px 120px 0px rgba(38, 51, 77, 0.05)',
                    marginTop: '40px',
                }}
            >
                {/* Toolbar wrapper - matching .rbc-calendar>div:first-child */}
                <div
                    style={{
                        background: 'transparent',
                        padding: '29px 29px 34px 29px',
                        border: 'none',
                        marginBottom: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Today button skeleton - matching .rbc-calendar>div:first-child>button */}
                    <div
                        style={{
                            borderRadius: '20px',
                            border: '2px solid #f5f6f7',
                            background: '#fff',
                            boxShadow: '0px 2px 5px 0px rgba(38, 51, 77, 0.03)',
                            color: '#6b7a99',
                            fontSize: '12px',
                            fontWeight: '700',
                            lineHeight: '20px',
                            padding: '8px 16px',
                            minWidth: '70px',
                            minHeight: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: 'pulse 1.5s ease-in-out infinite',
                        }}
                    >
                        <div
                            style={{
                                width: '50px',
                                height: '16px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        />
                    </div>
                    {/* Navigation and date label skeleton - matching .rbc-calendar>div:first-child div:nth-last-child(2) */}
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {/* Previous arrow skeleton */}
                        <div
                            style={{
                                position: 'relative',
                                width: '40px',
                                height: '40px',
                                margin: '0',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '50%',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        />
                        {/* Date label skeleton - matching span inside div:nth-last-child(2) */}
                        <div
                            style={{
                                color: '#6b7a99',
                                textAlign: 'center',
                                fontSize: '16px',
                                fontWeight: '500',
                                lineHeight: '20px',
                                margin: '0 37px',
                                minWidth: '150px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        >
                            <div
                                style={{
                                    width: '140px',
                                    height: '16px',
                                    backgroundColor: '#e5e7eb',
                                    borderRadius: '4px',
                                    animation: 'pulse 1.5s ease-in-out infinite',
                                }}
                            />
                        </div>
                        {/* Next arrow skeleton */}
                        <div
                            style={{
                                position: 'relative',
                                width: '40px',
                                height: '40px',
                                margin: '0',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '50%',
                                transform: 'rotate(180deg)',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        />
                    </div>
                    {/* View switcher buttons skeleton - matching .rbc-calendar>div:first-child>div:last-child */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '0',
                            border: '2px solid #f5f6f7',
                            background: '#fff',
                            borderRadius: '30px',
                            height: '36px',
                            alignItems: 'center',
                        }}
                    >
                        {['Week', 'Month', 'Day'].map((view, i) => (
                            <div
                                key={view}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: i === 0 ? '30px 0 0 30px' : i === 2 ? '0 30px 30px 0' : '0',
                                    backgroundColor: i === 1 ? '#6479f8' : '#fff',
                                    color: i === 1 ? '#fff' : '#adb8cc',
                                    fontSize: '14px',
                                    fontWeight: i === 1 ? '600' : '500',
                                    lineHeight: '20px',
                                    textTransform: 'uppercase',
                                    paddingLeft: i === 0 ? '20px' : '15px',
                                    paddingRight: i === 2 ? '20px' : '15px',
                                    borderLeft: i === 2 ? '2px solid #f5f6f7' : 'none',
                                    minWidth: i === 0 ? '60px' : i === 1 ? '70px' : '60px',
                                    minHeight: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    animation: 'pulse 1.5s ease-in-out infinite',
                                }}
                            >
                                <div
                                    style={{
                                        width: i === 0 ? '45px' : i === 1 ? '55px' : '40px',
                                        height: '16px',
                                        backgroundColor: i === 1 ? 'rgba(255, 255, 255, 0.7)' : '#e5e7eb',
                                        borderRadius: '4px',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Calendar grid skeleton - matching .rbc-month-view structure */}
                <div
                    className="rbc-month-view"
                    style={{
                        borderTop: '2px solid #f5f6f7',
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px',
                    }}
                >
                    {/* Day headers skeleton - matching .rbc-header */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(7, 1fr)',
                        }}
                    >
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                            <div
                                key={day}
                                className="rbc-header"
                                style={{
                                    padding: '25px 0',
                                    borderRight: index < 6 ? '1px solid #DDE3EA' : 'none',
                                    textAlign: 'center',
                                    backgroundColor: '#fff',
                                    color: '#6b7a99',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    lineHeight: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <div
                                    style={{
                                        width: '30px',
                                        height: '16px',
                                        backgroundColor: '#e5e7eb',
                                        borderRadius: '4px',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                    {/* Calendar cells skeleton - matching .rbc-month-row and .rbc-day-bg */}
                    {Array.from({ length: 5 }).map((_, rowIndex) => (
                        <div
                            key={rowIndex}
                            className="rbc-month-row"
                            style={{
                                minBlockSize: 'fit-content',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(7, 1fr)',
                                borderBottom: rowIndex < 4 ? '1px solid #DDE3EA' : 'none',
                            }}
                        >
                            {Array.from({ length: 7 }).map((_, colIndex) => {
                                const cellIndex = rowIndex * 7 + colIndex;
                                return (
                                    <div
                                        key={colIndex}
                                        className="rbc-day-bg"
                                        style={{
                                            minHeight: '120px',
                                            paddingTop: '9px',
                                            paddingRight: '6px',
                                            paddingBottom: '8px',
                                            paddingLeft: '6px',
                                            backgroundColor: '#fff',
                                            borderRight: colIndex < 6 ? '1px solid #DDE3EA' : 'none',
                                            position: 'relative',
                                        }}
                                    >
                                        {/* Day number skeleton */}
                                        <div
                                            style={{
                                                width: '23px',
                                                height: '23px',
                                                backgroundColor: '#e5e7eb',
                                                borderRadius: '50%',
                                                marginBottom: '8px',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                            }}
                                        />
                                        {/* Event skeletons */}
                                        {cellIndex % 5 === 0 && (
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '18px',
                                                    backgroundColor: '#e5e7eb',
                                                    borderRadius: '4px',
                                                    marginBottom: '4px',
                                                    animation: 'pulse 1.5s ease-in-out infinite',
                                                }}
                                            />
                                        )}
                                        {cellIndex % 7 === 0 && cellIndex > 0 && (
                                            <div
                                                style={{
                                                    width: '85%',
                                                    height: '18px',
                                                    backgroundColor: '#e5e7eb',
                                                    borderRadius: '4px',
                                                    marginBottom: '4px',
                                                    animation: 'pulse 1.5s ease-in-out infinite',
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.5;
                    }
                }
            `}</style>
        </div>
    );
};

/**
 * Filter Bar Skeleton - Shows skeleton for filter controls
 */
export const FilterBarSkeleton = () => {
    return (
        <>
            {/* Top section with buttons and search */}
            <div className='user-outer-overview report-top-button eport-top-button-new'>
                <div className='button-inner-report button-inner-report-new'>
                    {/* Create button skeleton */}
                    <div className="dropdown-container-cal">
                        <div
                            className="dropdown-button-cal"
                            style={{
                                backgroundColor: '#6479F8',
                                color: 'white',
                                border: 'none',
                                padding: '11.657px 19px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12.5px',
                                width: '120px',
                                height: '44px',
                                opacity: 0.7,
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        />
                    </div>
                    {/* Import button skeleton */}
                    <div className="button-inner-report" style={{ marginRight: '0px' }}>
                        <div
                            className="dropdown-button-cal"
                            style={{
                                backgroundColor: '#6479F8',
                                color: 'white',
                                border: 'none',
                                padding: '11.657px 19px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12.5px',
                                minWidth: '232px',
                                height: '44px',
                                opacity: 0.7,
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        />
                    </div>
                </div>
                {/* Search input skeleton */}
                <div className="search-sec">
                    <div className="search-container">
                        <div
                            className="search-input"
                            style={{
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                cursor: 'pointer',
                                paddingLeft: '58px',
                                paddingRight: '15px',
                                backgroundColor: '#EAEBEC',
                                borderRadius: '10px',
                                width: '247px',
                                height: '45px',
                                border: 'none',
                                fontWeight: '300',
                                fontSize: '19px',
                                lineHeight: '23.18px',
                                letterSpacing: '0.5px',
                                color: '#6A7988',
                                display: 'flex',
                                alignItems: 'center',
                                position: 'relative',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        >
                            {/* Search icon skeleton */}
                            <div
                                style={{
                                    position: 'absolute',
                                    left: '15px',
                                    top: '50%',
                                    width: '20px',
                                    height: '20px',
                                    backgroundColor: '#e5e7eb',
                                    borderRadius: '4px',
                                    animation: 'pulse 1.5s ease-in-out infinite',
                                }}
                            />
                            {/* Placeholder text skeleton */}
                            <div
                                style={{
                                    width: '100px',
                                    height: '18px',
                                    backgroundColor: '#e5e7eb',
                                    borderRadius: '4px',
                                    animation: 'pulse 1.5s ease-in-out infinite',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {/* Filter section */}
            <div className="calender-filter-outer">
                {/* User Visibility dropdown skeleton (for admin) */}
                <div className="filter-inner invoice-status">
                    <label htmlFor="user" style={{
                        color: '#282828',
                        fontSize: '13px',
                        fontStyle: 'normal',
                        fontWeight: '300',
                        lineHeight: '15.86px',
                        marginBottom: '8px',
                        display: 'block',
                    }}>User Visibility</label>
                    <div className="custom-dropdown1">
                        <div className="drop-down-inner">
                            <div className="selected-options1">
                                <div className="selected-option1">
                                    <div
                                        style={{
                                            width: "120px",
                                            height: "20px",
                                            borderRadius: "4px",
                                            backgroundColor: "#e5e7eb",
                                            animation: "pulse 1.5s ease-in-out infinite",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Categories section skeleton */}
                <div className="filter-inner filter-sec-category-calendar">
                    <div className="category-section">
                        <label style={{
                            color: '#282828',
                            fontSize: '13px',
                            fontStyle: 'normal',
                            fontWeight: '300',
                            lineHeight: '15.86px',
                            letterSpacing: '0.5px',
                            marginBottom: '7px',
                        }}>Categories</label>
                        <div className="category-box">
                            {['Holidays', 'Events', 'Anniversaries', 'Birthdays', 'Time-Off'].map((categoryName) => {
                                // Calculate approximate width based on actual text length and font styling
                                // font-size: 13px, font-weight: 500
                                const textWidths = {
                                    'Holidays': '68px',      // 8 characters
                                    'Events': '52px',         // 6 characters
                                    'Anniversaries': '102px', // 12 characters (longest)
                                    'Birthdays': '76px',      // 9 characters
                                    'Time-Off': '70px'        // 8 characters (with hyphen)
                                };
                                return (
                                    <div
                                        key={categoryName}
                                        className="category-pill"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            borderRadius: '12px',
                                            padding: '0',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: '17px',
                                                height: '17px',
                                                borderRadius: '4px',
                                                backgroundColor: '#e5e7eb',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                            }}
                                        />
                                        <div
                                            style={{
                                                width: textWidths[categoryName],
                                                height: '15.86px',
                                                backgroundColor: '#e5e7eb',
                                                borderRadius: '4px',
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                    }
                    50% {
                        opacity: 0.5;
                    }
                }
            `}</style>
        </>
    );
};

/**
 * Event List Skeleton - Shows skeleton for event list
 */
export const EventListSkeleton = () => {
    return (
        <div
            role="status"
            aria-label="Loading events"
            style={{
                padding: '16px',
            }}
        >
            {[1, 2, 3, 4, 5].map((i) => (
                <div
                    key={i}
                    style={{
                        display: 'flex',
                        gap: '12px',
                        padding: '12px',
                        marginBottom: '12px',
                        backgroundColor: CALENDAR_CONFIG.UI_COLORS.BG_WHITE,
                        borderRadius: '6px',
                        border: `1px solid ${CALENDAR_CONFIG.UI_COLORS.BORDER_LIGHT}`,
                    }}
                >
                    {/* Event icon skeleton */}
                    <div
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: '#e5e7eb',
                            borderRadius: '6px',
                            animation: 'pulse 1.5s ease-in-out infinite',
                        }}
                    />
                    {/* Event content skeleton */}
                    <div
                        style={{
                            flex: 1,
                        }}
                    >
                        <div
                            style={{
                                width: '60%',
                                height: '20px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                marginBottom: '8px',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        />
                        <div
                            style={{
                                width: '40%',
                                height: '16px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

/**
 * Inline Loading Indicator - Shows loading state inline
 */
export const InlineLoader = ({ message = "Loading..." }) => {
    return (
        <div
            role="status"
            aria-live="polite"
            aria-label={message}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                color: CALENDAR_CONFIG.UI_COLORS.TEXT_SECONDARY,
            }}
        >
            <div
                style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #e5e7eb',
                    borderTop: '2px solid #2563eb',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                }}
            />
            <span>{message}</span>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

/**
 * Calendar Overlay Skeleton - Shows loading overlay on top of calendar during data refresh
 * This is used when user selection changes and events are being refetched
 */
export const CalendarOverlaySkeleton = () => {
    return (
        <div
            role="status"
            aria-live="polite"
            aria-label="Loading calendar events"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(2px)',
                WebkitBackdropFilter: 'blur(2px)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
            }}
        >
            {/* Loading spinner */}
            <div
                style={{
                    width: '48px',
                    height: '48px',
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #2563eb',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    marginBottom: '16px',
                }}
            />
            {/* Loading text */}
            <p
                style={{
                    fontSize: '14px',
                    color: CALENDAR_CONFIG.UI_COLORS.TEXT_SECONDARY,
                    fontWeight: '500',
                    margin: 0,
                }}
            >
                Loading calendar events...
            </p>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

/**
 * Button Skeleton Loader - For loading states in buttons
 */
export const ButtonSkeleton = ({ width = '60px', height = '18px' }) => {
    return (
        <div
            style={{
                width: width,
                height: height,
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
            }}
        />
    );
};

/**
 * User Profile Skeleton - For loading user profile information
 */
export const UserProfileSkeleton = () => {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Avatar skeleton */}
            <div
                style={{
                    width: '35px',
                    height: '35px',
                    borderRadius: '50%',
                    backgroundColor: '#e5e7eb',
                    animation: 'pulse 1.5s ease-in-out infinite',
                }}
            />
            {/* Name skeleton */}
            <div
                style={{
                    width: '120px',
                    height: '18px',
                    backgroundColor: '#e5e7eb',
                    borderRadius: '4px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                }}
            />
        </div>
    );
};

/**
 * Event Field Skeleton - For loading event detail fields
 */
export const EventFieldSkeleton = () => {
    return (
        <div className="form-field-client">
            <div
                className="form-label"
                style={{
                    marginBottom: '8px',
                }}
            >
                <div
                    style={{
                        width: '80px',
                        height: '15px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                />
            </div>
            <div className="form-details">
                <div
                    style={{
                        width: '150px',
                        height: '18px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '4px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                />
            </div>
        </div>
    );
};

/**
 * Guest List Skeleton - For loading guest/attendee lists
 */
export const GuestListSkeleton = ({ count = 3 }) => {
    return (
        <div className="form-details">
            {Array.from({ length: count }).map((_, idx) => (
                <div
                    key={idx}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '15px',
                    }}
                >
                    <div style={{ position: 'relative' }}>
                        {/* Avatar skeleton */}
                        <div
                            style={{
                                width: '35px',
                                height: '35px',
                                borderRadius: '50%',
                                backgroundColor: '#e5e7eb',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        />
                    </div>
                    <div style={{ marginLeft: '10px' }}>
                        <div
                            style={{
                                width: `${100 + idx * 20}px`,
                                height: '18px',
                                backgroundColor: '#e5e7eb',
                                borderRadius: '4px',
                                animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default CalendarSkeleton;

